/**
 * Widget TypeScript Type Definitions
 *
 * Naming convention:
 *  - DTOs / data contracts shared with the server use the `I*` prefix
 *    (`IMarketEventDTO`, `IMarketDTO`) and live in `@repo/types`. The widget
 *    imports them directly — it does NOT redeclare its own copies.
 *  - Local widget types (component props, hook results, view enums) are
 *    unprefixed, matching the convention used by `apps/web` for the same
 *    kind of types.
 */

export interface WidgetAppProps {
  apiKey: string;
  tenantId: string;
  pageUrl: string;
  /**
   * Called once the data load leaves the loading state (ready, empty, or
   * error). The inline mount path uses this to release the space it reserved
   * for loading, so an empty/error result collapses instead of leaving a gap.
   */
  onSettled?: () => void;
}

export const WidgetState = {
  Loading: "loading",
  Ready: "ready",
  Error: "error",
  Empty: "empty",
} as const;
export type WidgetState = (typeof WidgetState)[keyof typeof WidgetState];

/**
 * Which surface is currently displayed once the widget is ready. Values
 * mirror the Figma state names; surfaces are mutually exclusive.
 */
export const WidgetView = {
  Pill: "pill",
  Preview: "preview",
  Card: "card",
} as const;
export type WidgetView = (typeof WidgetView)[keyof typeof WidgetView];

/**
 * Extracted content from the host page (input to the ingestion endpoint).
 */
export interface ExtractedContent {
  title: string;
  description: string;
  keywords: string[];
  headings: string[];
  mainContent: string;
  images: string[];
  structuredData: Record<string, unknown>[];
  url: string;
  path: string;
}
